import { useState, useMemo, useRef } from 'react';
import styles from './ExportPanel.module.css';
import { deliverableLinkedinUrl, personLocationOf } from '../lib/employmentStatus';

// Column headers shown in the preview table (abbreviated for space).
// The file itself is written by exportCSV / exportBatchCSV in VerifierPage.jsx, which pick columns
// by the index order this panel hands back - so their header lists must stay the same length and
// order as these. A Location column added here and not there is how every export ended in a
// column headed "undefined".
// City and State are the COMPANY's (Lead411 carries no employee location). Location is the
// PERSON's, from their LinkedIn profile or the company roster, when a check reached them. An
// export that stamped Rackspace's San Antonio on someone in Sydney is why the two are separate.
const BATCH_HEADERS = [
  'Company', 'First Name', 'Last Name', 'Title',
  'Employees', 'Revenue', 'Industry', 'Industry (Specific)', 'Website',
  'LinkedIn URL', 'Email', 'Email Status', 'Direct Dial', 'Mobile',
  'City', 'State', 'Location', 'Employment', 'Status',
];

const CHAT_HEADERS = [
  'First Name', 'Last Name', 'Title', 'Company',
  'Employees', 'Revenue', 'Industry', 'Industry (Specific)', 'Website',
  'LinkedIn URL', 'Email', 'Email Status', 'Direct Dial', 'Mobile',
  'City', 'State', 'Location', 'Employment',
];

const LI_URL_COL = 9; // LinkedIn URL is column 9 in both modes

// ── Preview row builders (no employment re-verification — preview only) ───────

function splitName(contact) {
  const first = contact.firstName || '';
  const last  = contact.lastName  || '';
  if (first || last) return { first, last };
  const parts = (contact.name || '').trim().split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' };
}

function quickEmpLabel(contact) {
  const check = contact?.employmentCheck;
  if (!check?.status) return (contact?.linkedinUrl ? 'Not Checked' : 'No LinkedIn');
  if (check.status === 'verified')  return 'Yes - Verified';
  if (check.status === 'rejected')  return `No - Now at ${check.currentCompany || 'Unknown'}`;
  return 'Unverifiable';
}

function verdictLabel(verdict) {
  if (verdict === 'valid')          return 'Verified';
  if (verdict === 'risky')          return 'Risky';
  if (verdict === 'linkedin-only')  return 'LinkedIn only - no email';
  if (verdict === 'invalid')        return 'Invalid';
  return '';
}

function makeBatchRows(batchResults) {
  const blanks = Array(BATCH_HEADERS.length - 2).fill('');
  return (batchResults?.results || []).map(r => {
    if (r.status !== 'found') {
      return {
        cells: [r.company || '', ...blanks, r.message || 'Not found'],
        _status: 'notfound', _company: r.company || '', _tier: null, _verdict: null,
      };
    }
    const c   = r.contact || {};
    const res = r.result  || {};
    const { first, last } = splitName(c);
    const direct = c.directDial  || (!c.mobilePhone ? c.phone || '' : '');
    const mobile = c.mobilePhone || '';
    return {
      cells: [
        r.company || '', first, last, c.title || '',
        c.companySize  || '', c.revenue   || '',
        c.industry     || '', c.industrySpecific || c.industry2 || '',
        c.domain       || '', deliverableLinkedinUrl(c), res.email || '',
        verdictLabel(res.verdict), direct, mobile,
        c.hqCity || '', c.hqState || '', personLocationOf(c),
        // "Both"-mode backfill rows re-deliver a contact the user already had (not charged again).
        quickEmpLabel(c), r.repeat ? 'Repeat' : 'Found',
      ],
      _status: 'found', _company: r.company || '',
      _tier: r.tier || null, _verdict: res.verdict || null,
    };
  });
}

function makeChatRows(contacts) {
  return (contacts || []).map(({ contact: c = {}, result: res = {} }) => {
    const { first, last } = splitName(c);
    const direct = c.directDial  || (!c.mobilePhone ? c.phone || '' : '');
    const mobile = c.mobilePhone || '';
    return {
      cells: [
        first, last, c.title || '', c.company || '',
        c.companySize  || '', c.revenue   || '',
        c.industry     || '', c.industrySpecific || c.industry2 || '',
        c.domain       || '', deliverableLinkedinUrl(c), res.email || '',
        verdictLabel(res.verdict), direct, mobile,
        c.hqCity || '', c.hqState || '', personLocationOf(c),
        quickEmpLabel(c),
      ],
      _status: 'found', _company: c.company || '',
      _tier: res.verdict === 'linkedin-only' ? 'linkedin_only' : null,
      _verdict: res.verdict || null,
    };
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ExportPanel({
  contacts,           // [{contact, result}] for chat mode
  batchResults,       // batchResults object for batch mode
  // Generic mode: a plain grid. When customHeaders is set the panel previews customRows
  // (string[][]) under those headers with all the same chrome - filename, format, column
  // reorder/hide, sort - and none of the contact-specific extras (employment note, LinkedIn
  // links, row tinting). Used by the look-alike list export and the enriched-file download.
  customHeaders,
  customRows,
  defaultFilename,
  canSplit,           // whether sheet-split options are shown
  isBatch,
  isDownloading,
  onClose,
  onDownload,         // (config) => void
  hubspotConnected,   // optional — show Push to HubSpot button when true
  onPushToHubSpot,    // optional — called with no args to trigger the push
  isPushingToHubspot, // optional — true while push is in flight
}) {
  const allHeaders = customHeaders || (isBatch ? BATCH_HEADERS : CHAT_HEADERS);

  const [filename,   setFilename]   = useState(defaultFilename || (isBatch ? 'repbuddy-batch' : 'repbuddy-contacts'));
  const [format,     setFormat]     = useState('csv');
  const [sheetSplit, setSheetSplit] = useState('single');
  const [colOrder,   setColOrder]   = useState(() => allHeaders.map((_, i) => i));
  const [hiddenCols, setHiddenCols] = useState(() => new Set());
  const [sortCol,    setSortCol]    = useState(null);
  const [sortDir,    setSortDir]    = useState('asc');

  const dragSrc = useRef(null);

  const rawRows = useMemo(
    () => customRows
      ? customRows.map(cells => ({ cells, _status: 'found', _company: '', _tier: null, _verdict: null }))
      : (isBatch ? makeBatchRows(batchResults) : makeChatRows(contacts)),
    [customRows, isBatch, batchResults, contacts],
  );

  const visibleCols = colOrder.filter(i => !hiddenCols.has(i));

  const sortedRows = useMemo(() => {
    if (sortCol === null || sortCol >= visibleCols.length) return rawRows;
    const origIdx = visibleCols[sortCol];
    return [...rawRows].sort((a, b) => {
      const av = String(a.cells[origIdx] ?? '');
      const bv = String(b.cells[origIdx] ?? '');
      const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base', numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rawRows, sortCol, sortDir, visibleCols]);

  function handleHeaderClick(visIdx) {
    if (sortCol === visIdx) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(visIdx); setSortDir('asc'); }
  }

  function toggleCol(origIdx) {
    setHiddenCols(prev => {
      const next = new Set(prev);
      next.has(origIdx) ? next.delete(origIdx) : next.add(origIdx);
      return next;
    });
  }

  function onDragStart(posIdx) { dragSrc.current = posIdx; }

  function onDrop(posIdx) {
    const from = dragSrc.current;
    if (from === null || from === posIdx) { dragSrc.current = null; return; }
    setColOrder(prev => {
      const arr = [...prev];
      const [item] = arr.splice(from, 1);
      arr.splice(posIdx, 0, item);
      return arr;
    });
    dragSrc.current = null;
  }

  function handleDownload() {
    onDownload({
      filename:   (filename.trim() || (isBatch ? 'repbuddy-batch' : 'repbuddy-contacts')),
      format,
      sheetSplit: (format === 'xlsx' && canSplit) ? sheetSplit : 'single',
      colOrder:   [...colOrder],
      hiddenCols: [...hiddenCols],
      sortCol,
      sortDir,
    });
  }

  function rowClass(row) {
    if (row._status === 'notfound')          return styles.rowNotFound;
    if (row._tier   === 'linkedin_only')     return styles.rowLinkedin;
    if (row._verdict === 'valid')            return styles.rowValid;
    if (row._verdict === 'risky')            return styles.rowRisky;
    return '';
  }

  const splitOptions = [
    { v: 'single',     l: 'Single sheet' },
    ...(isBatch ? [{ v: 'by-company', l: 'Split by company' }] : []),
    { v: 'by-status',  l: 'Split by status' },
  ];

  return (
    <div
      className={styles.backdrop}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.panel} role="dialog" aria-modal="true" aria-label="Export Preview">

        {/* Header */}
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>Export Preview</span>
          <span className={styles.rowBadge}>{rawRows.length} row{rawRows.length !== 1 ? 's' : ''}</span>
          {!customHeaders && <span className={styles.empNote}>Employment status re-verified on download</span>}
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={styles.panelBody}>
          {/* ── Sidebar ── */}
          <aside className={styles.sidebar}>

            <div className={styles.sideSection}>
              <div className={styles.sideLabel}>File name</div>
              <input
                className={styles.filenameInput}
                value={filename}
                onChange={e => setFilename(e.target.value)}
                placeholder="filename"
                spellCheck={false}
              />
            </div>

            <div className={styles.sideSection}>
              <div className={styles.sideLabel}>Format</div>
              <div className={styles.pill2}>
                <button
                  className={`${styles.pill2Btn} ${format === 'csv'  ? styles.pill2Active : ''}`}
                  onClick={() => setFormat('csv')}
                >CSV</button>
                <button
                  className={`${styles.pill2Btn} ${format === 'xlsx' ? styles.pill2Active : ''}`}
                  onClick={() => setFormat('xlsx')}
                >Excel (.xlsx)</button>
              </div>
            </div>

            {format === 'xlsx' && canSplit && (
              <div className={styles.sideSection}>
                <div className={styles.sideLabel}>Sheets</div>
                {splitOptions.map(({ v, l }) => (
                  <label key={v} className={styles.radioRow}>
                    <input
                      type="radio"
                      name="sheetSplit"
                      checked={sheetSplit === v}
                      onChange={() => setSheetSplit(v)}
                    />
                    <span>{l}</span>
                  </label>
                ))}
                {sheetSplit !== 'single' && (
                  <div className={styles.splitNote}>
                    All Contacts tab always included
                  </div>
                )}
              </div>
            )}

            <div className={`${styles.sideSection} ${styles.colSection}`}>
              <div className={styles.sideLabel}>
                Columns
                <span className={styles.sideHint}> — drag to reorder</span>
              </div>
              <div className={styles.colList}>
                {colOrder.map((origIdx, posIdx) => (
                  <div
                    key={origIdx}
                    className={`${styles.colItem} ${hiddenCols.has(origIdx) ? styles.colHidden : ''}`}
                    draggable
                    onDragStart={() => onDragStart(posIdx)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => onDrop(posIdx)}
                  >
                    <span className={styles.dragHandle} title="Drag to reorder">⠿</span>
                    <input
                      type="checkbox"
                      checked={!hiddenCols.has(origIdx)}
                      onChange={() => toggleCol(origIdx)}
                    />
                    <span className={styles.colName}>{allHeaders[origIdx]}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.sideFooter}>
              <button
                className={styles.downloadBtn}
                onClick={handleDownload}
                disabled={isDownloading || visibleCols.length === 0}
              >
                {isDownloading ? 'Preparing…' : `Download ${format === 'xlsx' ? 'Excel' : 'CSV'}`}
              </button>
              {hubspotConnected && onPushToHubSpot && (
                <button
                  className={styles.hubspotBtn}
                  onClick={onPushToHubSpot}
                  disabled={isPushingToHubspot || isDownloading || rawRows.length === 0}
                >
                  {isPushingToHubspot ? 'Pushing…' : 'Push to HubSpot'}
                </button>
              )}
            </div>
          </aside>

          {/* ── Preview table ── */}
          <div className={styles.tableWrap}>
            {sortedRows.length === 0 ? (
              <div className={styles.empty}>{customHeaders ? 'Nothing to preview.' : 'No contacts to preview.'}</div>
            ) : (
              <table className={styles.previewTable}>
                <thead>
                  <tr>
                    {visibleCols.map((origIdx, visIdx) => (
                      <th
                        key={origIdx}
                        className={`${styles.th} ${sortCol === visIdx ? styles.thSorted : ''}`}
                        onClick={() => handleHeaderClick(visIdx)}
                        title="Click to sort"
                      >
                        {allHeaders[origIdx]}
                        {sortCol === visIdx
                          ? <span className={styles.sortArrow}>{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>
                          : <span className={styles.sortHint}> ↕</span>
                        }
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, i) => (
                    <tr key={i} className={`${styles.tr} ${rowClass(row)}`}>
                      {visibleCols.map(origIdx => (
                        <td key={origIdx} className={styles.td}>
                          {!customHeaders && origIdx === LI_URL_COL && row.cells[origIdx]
                            ? <a
                                href={row.cells[origIdx]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.liLink}
                                onClick={e => e.stopPropagation()}
                              >LinkedIn ↗</a>
                            : (row.cells[origIdx] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
